import type {
  AmhsLiveInventory,
  AmhsLiveInventoryGate,
  AmhsLiveInventoryStocker,
  AmhsStockerDropSummary,
  LiveOhtRuntimeFrame,
  OhtTimelineEvent,
} from '../../types/amhsStream';
import {
  STOCKER_DROP_LP_RELEASE_PROGRESS,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from '../../components/canvas/amhs/Stocker/stockerTransferTiming';
import { transferAnimationPipelineStore } from '../../store/transferAnimationPipelineStore';
import { liveStockerColumn } from './liveStockerInventory';

/**
 * Inventory frames land on the simulation clock. Publishing one as it arrives
 * can change a stocker's count before the OHT has visibly handed the FOUP over.
 * These helpers keep the affected stocker/gate pinned to its last published
 * values until the OHT releases on the load port (DROP) or takes from the load
 * port (PICKUP).
 */

export type StockerColumnHolds = {
  raw?: 'PICKUP' | 'DROP';
  processed?: 'PICKUP' | 'DROP';
};

export type InventoryHoldTargets = {
  stockerIds: ReadonlySet<string>;
  gatePortIds: ReadonlySet<string>;
  /** Per-column handoff direction while a stocker row is pinned. */
  stockerColumnHolds: ReadonlyMap<string, StockerColumnHolds>;
};

export type InventoryHoldContext = {
  ohtTimelines?: Record<string, OhtTimelineEvent[]>;
  lastStockerDrops?: Readonly<Record<string, AmhsStockerDropSummary>>;
  releasedStockers?: ReadonlyMap<string, AmhsLiveInventoryStocker>;
};

export const NO_INVENTORY_HOLD: InventoryHoldTargets = {
  stockerIds: new Set<string>(),
  gatePortIds: new Set<string>(),
  stockerColumnHolds: new Map(),
};

function stockerTotalOccupancy(stocker: AmhsLiveInventoryStocker | undefined): number {
  if (!stocker) return 0;
  if (typeof stocker.occupancy === 'number' && Number.isFinite(stocker.occupancy)) {
    return stocker.occupancy;
  }
  const raw = stocker.rawOccupancy ?? stocker.newOccupancy ?? 0;
  const processed = stocker.processedOccupancy ?? 0;
  return raw + processed;
}

function hasColumnSplit(stocker: AmhsLiveInventoryStocker): boolean {
  return (
    typeof stocker.rawOccupancy === 'number' ||
    typeof stocker.newOccupancy === 'number' ||
    typeof stocker.processedOccupancy === 'number' ||
    (stocker.columns?.length ?? 0) >= 2
  );
}

function sameStockerRow(
  a: AmhsLiveInventoryStocker,
  b: AmhsLiveInventoryStocker | undefined
): boolean {
  if (!b || a.stockerId !== b.stockerId) return false;
  return (
    columnCount(a, 'raw') === columnCount(b, 'raw') &&
    columnCount(a, 'processed') === columnCount(b, 'processed') &&
    stockerTotalOccupancy(a) === stockerTotalOccupancy(b)
  );
}

function pinStockerOccupancy(
  stocker: AmhsLiveInventoryStocker,
  occupancy: number
): AmhsLiveInventoryStocker {
  const next = Math.max(0, occupancy);
  if (typeof stocker.occupancy === 'number' && !hasColumnSplit(stocker)) {
    return { ...stocker, occupancy: next };
  }
  const raw = stocker.rawOccupancy ?? stocker.newOccupancy ?? 0;
  const processed = stocker.processedOccupancy ?? 0;
  const delta = stockerTotalOccupancy(stocker) - next;
  if (delta <= 0) return withColumnCounts(stocker, raw, processed);
  if (raw >= delta) return withColumnCounts(stocker, raw - delta, processed);
  return withColumnCounts(stocker, 0, Math.max(0, processed - (delta - raw)));
}

function columnCount(
  stocker: AmhsLiveInventoryStocker | undefined,
  kind: 'raw' | 'processed'
): number {
  if (!stocker) return 0;
  const occupancy = liveStockerColumn(stocker, kind).occupancy;
  return typeof occupancy === 'number' && Number.isFinite(occupancy) ? occupancy : 0;
}

function withColumnCounts(
  stocker: AmhsLiveInventoryStocker,
  raw: number,
  processed: number
): AmhsLiveInventoryStocker {
  const nextRaw = Math.max(0, raw);
  const nextProcessed = Math.max(0, processed);
  return {
    ...stocker,
    rawOccupancy: nextRaw,
    newOccupancy: nextRaw,
    processedOccupancy: nextProcessed,
    occupancy: nextRaw + nextProcessed,
  };
}

function normalizeHoldColumn(value: unknown): 'raw' | 'processed' | undefined {
  const kind = String(value ?? '')
    .trim()
    .toLowerCase();
  if (kind === 'raw' || kind === 'new') return 'raw';
  if (kind === 'processed' || kind === 'finished') return 'processed';
  return undefined;
}

/** Resolve raw vs processed for a hold from stream port metadata or LP suffix. */
export function resolveHoldColumn(
  portId: string,
  inventory: AmhsLiveInventory | null,
  stockerId: string
): 'raw' | 'processed' | undefined {
  const stocker = inventory?.stockers?.find((row) => row.stockerId === stockerId);
  const fromPort = stocker?.ports?.find((port) => port.loadPortId === portId)?.column;
  const normalized = normalizeHoldColumn(fromPort);
  if (normalized) return normalized;
  const match = /-LP-(\d+)$/i.exec(portId.trim());
  if (!match) return undefined;
  const dock = Number(match[1]);
  if (!Number.isFinite(dock) || dock <= 0) return undefined;
  if (dock <= 2) return 'raw';
  if (dock <= 4) return 'processed';
  return undefined;
}

function mergeStockerColumnHold(
  holds: Map<string, StockerColumnHolds>,
  stockerId: string,
  portId: string,
  action: 'PICKUP' | 'DROP',
  inventory: AmhsLiveInventory | null
): void {
  const column = resolveHoldColumn(portId, inventory, stockerId) ?? 'raw';
  const existing = holds.get(stockerId) ?? {};
  holds.set(stockerId, { ...existing, [column]: action });
}

function applyStockerColumnHolds(
  stream: AmhsLiveInventoryStocker,
  released: AmhsLiveInventoryStocker | undefined,
  holds: StockerColumnHolds
): AmhsLiveInventoryStocker {
  const streamRaw = columnCount(stream, 'raw');
  const streamProcessed = columnCount(stream, 'processed');
  const relRaw = columnCount(released, 'raw');
  const relProcessed = columnCount(released, 'processed');

  let raw = streamRaw;
  let processed = streamProcessed;

  if (holds.raw === 'DROP') raw = Math.min(streamRaw, relRaw);
  else if (holds.raw === 'PICKUP') raw = Math.max(streamRaw, relRaw);

  if (holds.processed === 'DROP') processed = Math.min(streamProcessed, relProcessed);
  else if (holds.processed === 'PICKUP') processed = Math.max(streamProcessed, relProcessed);

  return withColumnCounts(stream, raw, processed);
}

/** Synthesize a pre-commit baseline when inventory landed before hold metadata existed. */
function synthesizeReleasedBeforeCommit(
  stream: AmhsLiveInventoryStocker,
  holds: StockerColumnHolds
): AmhsLiveInventoryStocker {
  let raw = columnCount(stream, 'raw');
  let processed = columnCount(stream, 'processed');
  if (holds.raw === 'DROP') raw = Math.max(0, raw - 1);
  else if (holds.raw === 'PICKUP') raw += 1;
  if (holds.processed === 'DROP') processed = Math.max(0, processed - 1);
  else if (holds.processed === 'PICKUP') processed += 1;
  return withColumnCounts(stream, raw, processed);
}

const PORT_OWNER_SUFFIX = /^(.*)-LP-\d+$/i;

function canonicalStockerId(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

function stockerIdByPortId(inventory: AmhsLiveInventory | null): Map<string, string> {
  const byPort = new Map<string, string>();
  for (const stocker of inventory?.stockers ?? []) {
    if (!stocker.stockerId) continue;
    for (const port of stocker.ports ?? []) {
      if (port.loadPortId) byPort.set(port.loadPortId, stocker.stockerId);
    }
  }
  return byPort;
}

/** Map a streamed transfer port id (ST-1-LP-4, layout uuid, etc.) to a stocker row. */
export function resolveStockerIdForPort(
  portId: string,
  stationId: string | undefined,
  inventory: AmhsLiveInventory | null,
  knownStockerIds: ReadonlySet<string>
): string | undefined {
  if (stationId && knownStockerIds.has(stationId)) return stationId;
  const fromPort = stockerIdByPortId(inventory).get(portId);
  if (fromPort) return fromPort;
  const portOwnerId = PORT_OWNER_SUFFIX.exec(portId.trim())?.[1];
  if (!portOwnerId) return undefined;
  if (knownStockerIds.has(portOwnerId)) return portOwnerId;
  const canonicalOwner = canonicalStockerId(portOwnerId);
  for (const stockerId of knownStockerIds) {
    if (canonicalStockerId(stockerId) === canonicalOwner) return stockerId;
  }
  return undefined;
}

function resolveDropStockerId(
  event: Extract<OhtTimelineEvent, { type: 'transfer_event' }>,
  inventory: AmhsLiveInventory | null,
  _byPort: Map<string, string>,
  knownStockerIds: ReadonlySet<string>
): string | undefined {
  return resolveStockerIdForPort(
    event.frame.portId,
    event.frame.stockerId,
    inventory,
    knownStockerIds
  );
}

/** True while a DROP handoff has not passed the LP release milestone (0.35). */
export function isDropLpReleasePending(
  drop: { foupId: string; ohtId: string; portId: string; simTimeS?: number },
  frame: LiveOhtRuntimeFrame | null
): boolean {
  for (const snapshot of Object.values(frame?.byOhtId ?? {})) {
    if (snapshot.ohtId !== drop.ohtId) continue;
    const transfer = snapshot.transfer;
    if (
      transfer &&
      transfer.action === 'DROP' &&
      transfer.portId === drop.portId &&
      transfer.foupId === drop.foupId
    ) {
      return transfer.progress01 + 1e-6 < STOCKER_DROP_LP_RELEASE_PROGRESS;
    }
    // Stream committed early while the OHT is still inbound with the FOUP.
    if (
      snapshot.carriedFoupId === drop.foupId &&
      snapshot.state === 'MOVING' &&
      drop.simTimeS != null &&
      frame &&
      frame.globalSimTimeS + 1e-6 >= drop.simTimeS
    ) {
      return true;
    }
    return false;
  }
  if (
    drop.simTimeS != null &&
    frame &&
    frame.globalSimTimeS + 1e-6 >= drop.simTimeS
  ) {
    return true;
  }
  return false;
}

/** True while a PICKUP handoff has not passed the OHT take milestone (0.75). */
export function isPickupLpTakePending(
  pickup: { foupId: string; portId: string; ohtId?: string; startS?: number },
  frame: LiveOhtRuntimeFrame | null
): boolean {
  for (const snapshot of Object.values(frame?.byOhtId ?? {})) {
    if (pickup.ohtId && snapshot.ohtId !== pickup.ohtId) continue;
    const transfer = snapshot.transfer;
    if (
      transfer &&
      transfer.action === 'PICKUP' &&
      transfer.portId === pickup.portId &&
      transfer.foupId === pickup.foupId
    ) {
      return transfer.progress01 + 1e-6 < STOCKER_PICKUP_OHT_TAKE_PROGRESS;
    }
    if (snapshot.carriedFoupId === pickup.foupId) return false;
    if (
      pickup.startS != null &&
      frame &&
      frame.globalSimTimeS + 1e-6 >= pickup.startS
    ) {
      return true;
    }
    return false;
  }
  if (
    pickup.startS != null &&
    frame &&
    frame.globalSimTimeS + 1e-6 >= pickup.startS
  ) {
    return true;
  }
  return false;
}

/** Stocker rows that should stay pinned while an OHT is still carrying a DROP. */
function stockerIdsFromInFlightDrops(
  frame: LiveOhtRuntimeFrame | null,
  inventory: AmhsLiveInventory | null,
  ohtTimelines: Record<string, OhtTimelineEvent[]>
): Set<string> {
  const stockerIds = new Set<string>();
  const globalS = frame?.globalSimTimeS;
  if (globalS == null) return stockerIds;

  const byPort = stockerIdByPortId(inventory);
  const knownStockerIds = new Set(
    (inventory?.stockers ?? []).map((stocker) => stocker.stockerId).filter(Boolean)
  );

  for (const [ohtId, events] of Object.entries(ohtTimelines)) {
    const snapshot = frame?.byOhtId[ohtId];
    const carriedFoupId = snapshot?.carriedFoupId;
    if (!carriedFoupId) continue;

    for (const event of events) {
      if (event.type !== 'transfer_event' || event.action !== 'DROP') continue;
      if (event.frame.foupId !== carriedFoupId) continue;
      if (globalS + 1e-6 < event.startS) continue;

      const stockerId = resolveDropStockerId(event, inventory, byPort, knownStockerIds);
      if (!stockerId) continue;

      const transfer = snapshot?.transfer;
      if (
        transfer &&
        transfer.action === 'DROP' &&
        transfer.foupId === carriedFoupId &&
        transfer.progress01 + 1e-6 >= STOCKER_DROP_LP_RELEASE_PROGRESS
      ) {
        continue;
      }

      stockerIds.add(stockerId);
    }
  }

  return stockerIds;
}

/** Hold while a streamed DROP is known but the OHT handoff has not finished. */
function stockerIdsFromLastStockerDrops(
  frame: LiveOhtRuntimeFrame | null,
  lastStockerDrops: Readonly<Record<string, AmhsStockerDropSummary>>
): Set<string> {
  const stockerIds = new Set<string>();
  for (const drop of Object.values(lastStockerDrops)) {
    if (!drop.stockerId) continue;
    if (
      isDropLpReleasePending(
        {
          foupId: drop.foupId,
          ohtId: drop.ohtId,
          portId: drop.portId,
          simTimeS: drop.simTimeS,
        },
        frame
      )
    ) {
      stockerIds.add(drop.stockerId);
    }
  }
  return stockerIds;
}

/**
 * Hold when the inventory frame already committed a higher count but the
 * matching DROP animation has not landed yet.
 */
function stockerIdsFromEarlyInventoryCommit(
  frame: LiveOhtRuntimeFrame | null,
  inventory: AmhsLiveInventory | null,
  ohtTimelines: Record<string, OhtTimelineEvent[]>,
  releasedStockers: ReadonlyMap<string, AmhsLiveInventoryStocker>
): Set<string> {
  const stockerIds = new Set<string>();
  const byPort = stockerIdByPortId(inventory);
  const knownStockerIds = new Set(
    (inventory?.stockers ?? []).map((stocker) => stocker.stockerId).filter(Boolean)
  );

  for (const stocker of inventory?.stockers ?? []) {
    const released = releasedStockers.get(stocker.stockerId);
    const rawOcc = stockerTotalOccupancy(stocker);
    const releasedOcc = stockerTotalOccupancy(released);
    if (rawOcc <= releasedOcc) continue;

    for (const events of Object.values(ohtTimelines)) {
      for (const event of events) {
        if (event.type !== 'transfer_event' || event.action !== 'DROP') continue;
        const dropStockerId = resolveDropStockerId(event, inventory, byPort, knownStockerIds);
        if (dropStockerId !== stocker.stockerId) continue;
        if (
          isDropLpReleasePending(
            {
              foupId: event.frame.foupId,
              ohtId: event.ohtId,
              portId: event.frame.portId,
            },
            frame
          )
        ) {
          stockerIds.add(stocker.stockerId);
        }
      }
    }
  }

  return stockerIds;
}

/** Pin columns when the stream leads/lags a pending handoff on the timeline. */
function stockerColumnHoldsFromStreamLag(
  frame: LiveOhtRuntimeFrame | null,
  inventory: AmhsLiveInventory | null,
  ohtTimelines: Record<string, OhtTimelineEvent[]>,
  releasedStockers: ReadonlyMap<string, AmhsLiveInventoryStocker>
): Map<string, StockerColumnHolds> {
  const holds = new Map<string, StockerColumnHolds>();
  const knownStockerIds = new Set(
    (inventory?.stockers ?? []).map((stocker) => stocker.stockerId).filter(Boolean)
  );

  for (const stocker of inventory?.stockers ?? []) {
    const released = releasedStockers.get(stocker.stockerId);
    if (!released) continue;

    for (const kind of ['raw', 'processed'] as const) {
      const streamCol = columnCount(stocker, kind);
      const relCol = columnCount(released, kind);
      if (streamCol === relCol) continue;

      const action: 'PICKUP' | 'DROP' = streamCol > relCol ? 'DROP' : 'PICKUP';

      for (const events of Object.values(ohtTimelines)) {
        for (const event of events) {
          if (event.type !== 'transfer_event' || event.action !== action) continue;
          const stockerId = resolveStockerIdForPort(
            event.frame.portId,
            event.frame.stockerId,
            inventory,
            knownStockerIds
          );
          if (stockerId !== stocker.stockerId) continue;
          const column = resolveHoldColumn(event.frame.portId, inventory, stockerId);
          if (column !== kind) continue;

          const pending =
            action === 'DROP'
              ? isDropLpReleasePending(
                  {
                    foupId: event.frame.foupId,
                    ohtId: event.ohtId,
                    portId: event.frame.portId,
                    simTimeS: event.startS,
                  },
                  frame
                )
              : isPickupLpTakePending(
                  {
                    foupId: event.frame.foupId,
                    ohtId: event.ohtId,
                    portId: event.frame.portId,
                    startS: event.startS,
                  },
                  frame
                );
          if (!pending) continue;

          const existing = holds.get(stockerId) ?? {};
          holds.set(stockerId, { ...existing, [kind]: action });
        }
      }
    }
  }

  return holds;
}

/** Stockers and gates that currently have an OHT mid-handoff against them. */
export function resolveInventoryHoldTargets(
  frame: LiveOhtRuntimeFrame | null,
  inventory: AmhsLiveInventory | null,
  context: InventoryHoldContext = {}
): InventoryHoldTargets {
  const ohtTimelines = context.ohtTimelines ?? {};
  const lastStockerDrops = context.lastStockerDrops ?? {};
  const releasedStockers = context.releasedStockers ?? new Map<string, AmhsLiveInventoryStocker>();

  const stockerIds = new Set<string>();
  const stockerColumnHolds = new Map<string, StockerColumnHolds>();
  const gatePortIds = new Set<string>();
  const byPort = stockerIdByPortId(inventory);
  const knownStockerIds = new Set(
    (inventory?.stockers ?? []).map((stocker) => stocker.stockerId).filter(Boolean)
  );

  const pinStocker = (
    stockerId: string,
    portId: string,
    action: 'PICKUP' | 'DROP'
  ): void => {
    stockerIds.add(stockerId);
    mergeStockerColumnHold(stockerColumnHolds, stockerId, portId, action, inventory);
  };

  for (const snapshot of Object.values(frame?.byOhtId ?? {})) {
    const transfer = snapshot.transfer;
    if (transfer) {
      const stockerId = resolveStockerIdForPort(
        transfer.portId,
        transfer.stationId,
        inventory,
        knownStockerIds
      );
      if (!stockerId) continue;

      if (
        transfer.action === 'DROP' &&
        transfer.progress01 + 1e-6 < STOCKER_DROP_LP_RELEASE_PROGRESS
      ) {
        pinStocker(stockerId, transfer.portId, 'DROP');
      } else if (
        transfer.action === 'PICKUP' &&
        isPickupLpTakePending(
          { foupId: transfer.foupId, portId: transfer.portId },
          frame
        )
      ) {
        pinStocker(stockerId, transfer.portId, 'PICKUP');
      }
    }
    const gate = snapshot.gateTransfer;
    if (gate && gate.status === 'TRANSFERRING' && gate.portId) {
      gatePortIds.add(gate.portId);
    }
  }

  for (const pipelineStockerId of transferAnimationPipelineStore.getInventoryHoldStockerIds()) {
    stockerIds.add(pipelineStockerId);
    if (!knownStockerIds.has(pipelineStockerId)) {
      for (const [portId, mappedStockerId] of byPort) {
        if (
          portId.startsWith(`${pipelineStockerId}-`) ||
          portId.startsWith(`${pipelineStockerId}_`)
        ) {
          stockerIds.add(mappedStockerId);
        }
      }
    }
  }

  for (const stockerId of stockerIdsFromInFlightDrops(frame, inventory, ohtTimelines)) {
    stockerIds.add(stockerId);
  }
  for (const stockerId of stockerIdsFromLastStockerDrops(frame, lastStockerDrops)) {
    stockerIds.add(stockerId);
    for (const drop of Object.values(lastStockerDrops)) {
      if (drop.stockerId === stockerId && isDropLpReleasePending(drop, frame)) {
        mergeStockerColumnHold(stockerColumnHolds, stockerId, drop.portId, 'DROP', inventory);
      }
    }
  }
  for (const stockerId of stockerIdsFromEarlyInventoryCommit(
    frame,
    inventory,
    ohtTimelines,
    releasedStockers
  )) {
    stockerIds.add(stockerId);
  }

  for (const stockerId of stockerIds) {
    if (stockerColumnHolds.has(stockerId)) continue;
    for (const events of Object.values(ohtTimelines)) {
      for (const event of events) {
        if (event.type !== 'transfer_event') continue;
        const dropStockerId = resolveDropStockerId(event, inventory, byPort, knownStockerIds);
        if (dropStockerId !== stockerId) continue;
        mergeStockerColumnHold(
          stockerColumnHolds,
          stockerId,
          event.frame.portId,
          event.action,
          inventory
        );
        break;
      }
      if (stockerColumnHolds.has(stockerId)) break;
    }
  }

  for (const [stockerId, columnHolds] of stockerColumnHoldsFromStreamLag(
    frame,
    inventory,
    ohtTimelines,
    releasedStockers
  )) {
    stockerIds.add(stockerId);
    const existing = stockerColumnHolds.get(stockerId) ?? {};
    stockerColumnHolds.set(stockerId, { ...existing, ...columnHolds });
  }

  if (stockerIds.size === 0 && gatePortIds.size === 0) return NO_INVENTORY_HOLD;
  return { stockerIds, gatePortIds, stockerColumnHolds };
}

/**
 * Substitutes held rows with their last released copy. `released` maps are
 * mutated so an unheld row always publishes — and becomes the value the next
 * hold pins to.
 */
export function applyInventoryHold(
  raw: AmhsLiveInventory | null,
  targets: InventoryHoldTargets,
  releasedStockers: Map<string, AmhsLiveInventoryStocker>,
  releasedGates: Map<string, AmhsLiveInventoryGate>
): AmhsLiveInventory | null {
  if (!raw) return null;

  let changed = false;

  const stockers = raw.stockers?.map((stocker) => {
    if (!targets.stockerIds.has(stocker.stockerId)) {
      releasedStockers.set(stocker.stockerId, stocker);
      return stocker;
    }

    let released = releasedStockers.get(stocker.stockerId);
    const streamOcc = stockerTotalOccupancy(stocker);
    const releasedOcc = stockerTotalOccupancy(released);

    if (!hasColumnSplit(stocker)) {
      if (released && streamOcc < releasedOcc) {
        changed = true;
        return released;
      }
      if (!released || releasedOcc >= streamOcc) {
        const pinOcc =
          releasedOcc > 0 && releasedOcc < streamOcc
            ? releasedOcc
            : Math.max(0, streamOcc - 1);
        released = pinStockerOccupancy(stocker, pinOcc);
        releasedStockers.set(stocker.stockerId, released);
      }
      if (released === stocker) return stocker;
      changed = true;
      return released;
    }

    const columnHolds = targets.stockerColumnHolds.get(stocker.stockerId) ?? {};
    if (!released || releasedOcc >= streamOcc) {
      released = synthesizeReleasedBeforeCommit(stocker, columnHolds);
      releasedStockers.set(stocker.stockerId, released);
    }

    const held = applyStockerColumnHolds(stocker, released, columnHolds);
    if (sameStockerRow(held, stocker)) return stocker;
    if (sameStockerRow(held, released)) {
      changed = true;
      return released!;
    }
    changed = true;
    return held;
  });

  const gates = raw.gates?.map((gate) => {
    if (!targets.gatePortIds.has(gate.portId)) {
      releasedGates.set(gate.portId, gate);
      return gate;
    }
    const held = releasedGates.get(gate.portId);
    if (!held || held === gate) return gate;
    changed = true;
    return held;
  });

  if (!changed) return raw;
  return {
    ...raw,
    ...(stockers ? { stockers } : {}),
    ...(gates ? { gates } : {}),
  };
}

function sameRows<T>(a: readonly T[] | undefined, b: readonly T[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((row, index) => row === b[index]);
}

/**
 * Held snapshots are rebuilt every animation frame, so consumers need a stable
 * reference while nothing they render has actually changed.
 */
export function sameInventorySnapshot(
  a: AmhsLiveInventory | null,
  b: AmhsLiveInventory | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const key of keys) {
    if (key === 'stockers' || key === 'gates') continue;
    if (a[key] !== b[key]) return false;
  }
  return sameRows(a.stockers, b.stockers) && sameRows(a.gates, b.gates);
}
